import httpClient from '../../../../api/httpClient';
import API_CONFIG from '../../../../config/api.config';
import { 
  Location, 
  LocationFormData, 
  LocationListResponse, 
  LocationFilterParams 
} from '../types/location.types';

const ENDPOINT = API_CONFIG.ENDPOINTS.LOCATIONS || '/api/locations';

export const locationsApi = {
  /**
   * Get paginated list of locations with optional filters
   */
  getAll: async (params?: LocationFilterParams): Promise<LocationListResponse> => {
    const response = await httpClient.get<{ data: Location[]; total: number }>(ENDPOINT, { params });
    
    // Handle the response from the backend
    const data = response.data?.data || [];
    const total = response.data?.total || 0;
    const limit = params?.limit || 10;
    const page = params?.page || 1;
    
    return {
      data,
      total,
      page,
      limit,
      totalPages: Math.ceil(total / limit) || 1
    };
  },

  /**
   * Get a single location by ID
   */
  getById: async (id: string): Promise<Location> => {
    const response = await httpClient.get<Location>(`${ENDPOINT}/${id}`);
    return response.data;
  },

  /**
   * Create a new location
   */
  create: async (location: LocationFormData): Promise<Location> => {
    const response = await httpClient.post<Location>(ENDPOINT, location);
    return response.data;
  },

  /**
   * Update an existing location
   */
  update: async (id: string, location: Partial<LocationFormData>): Promise<Location> => {
    const response = await httpClient.patch<Location>(`${ENDPOINT}/${id}`, location);
    return response.data;
  },

  /**
   * Delete a location
   */
  delete: async (id: string): Promise<void> => {
    await httpClient.delete(`${ENDPOINT}/${id}`);
  },

  /**
   * Get locations by facility
   */
  getByFacility: async (facilityId: string, params?: LocationFilterParams): Promise<LocationListResponse> => {
    const response = await httpClient.get<Location[]>(`${ENDPOINT}/facility/${facilityId}`, { params });
    return {
      data: response.data,
      total: response.data.length,
      page: params?.page || 1,
      limit: params?.limit || 10,
      totalPages: Math.ceil(response.data.length / (params?.limit || 10))
    };
  },

  /**
   * Get child locations for a parent location
   */
  getChildren: async (parentId: string): Promise<Location[]> => {
    const response = await httpClient.get<Location[]>(`${ENDPOINT}/${parentId}/children`);
    return response.data;
  },
};
